import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User, UserManager } from "oidc-client-ts";

import type { EnvName } from "../config/envs";
import { useEnv } from "../env/EnvProvider";
import { getUserManager } from "./oidc";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  roles: string[];
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

/**
 * Pulls realm_access.roles off an oidc-client-ts User. Keycloak puts
 * realm roles under `profile.realm_access.roles`. Defensive against
 * shape drift — returns an empty array on anything unexpected.
 */
function extractRoles(user: User | null): string[] {
  if (!user) return [];
  const profile = user.profile as Record<string, unknown> | undefined;
  const realmAccess = profile?.["realm_access"];
  if (!realmAccess || typeof realmAccess !== "object") return [];
  const roles = (realmAccess as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === "string");
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const env = useEnv();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const managerRef = useRef<UserManager | null>(null);

  useEffect(() => {
    let cancelled = false;
    const manager = getUserManager(env);
    managerRef.current = manager;
    setStatus("loading");

    const handleLoaded = (u: User) => {
      if (cancelled) return;
      setUser(u);
      setStatus("authenticated");
    };
    const handleUnloaded = () => {
      if (cancelled) return;
      setUser(null);
      setStatus("unauthenticated");
    };
    const handleExpired = () => {
      // Silent renew is enabled; if it fails the user will be unloaded.
      // Nothing to do here besides logging.
      console.warn("[auth] access token expired");
    };

    manager.events.addUserLoaded(handleLoaded);
    manager.events.addUserUnloaded(handleUnloaded);
    manager.events.addAccessTokenExpired(handleExpired);

    manager
      .getUser()
      .then((current) => {
        if (cancelled) return;
        if (current && !current.expired) {
          setUser(current);
          setStatus("authenticated");
        } else {
          setUser(null);
          setStatus("unauthenticated");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[auth] getUser failed", err);
        setUser(null);
        setStatus("unauthenticated");
      });

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(handleLoaded);
      manager.events.removeUserUnloaded(handleUnloaded);
      manager.events.removeAccessTokenExpired(handleExpired);
    };
  }, [env]);

  const signIn = useCallback(async () => {
    const manager = managerRef.current;
    if (!manager) return;
    await manager.signinRedirect();
  }, []);

  const signOut = useCallback(async () => {
    const manager = managerRef.current;
    if (!manager) return;
    try {
      // signoutRedirect reads the stored id_token, then clears the user.
      await manager.signoutRedirect();
    } catch (err) {
      await manager.removeUser();
      console.warn("[auth] signoutRedirect failed", err);
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const manager = managerRef.current;
    if (!manager) return null;
    const current = await manager.getUser();
    if (!current || current.expired) return null;
    return current.access_token ?? null;
  }, []);

  const roles = useMemo(() => extractRoles(user), [user]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, roles, signIn, signOut, getAccessToken }),
    [user, status, roles, signIn, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Realm roles from the current user's JWT, or `[]` when signed out. */
export function useRoles(): string[] {
  return useAuth().roles;
}

/** True when the current user carries the given realm role. */
export function useHasRole(role: string): boolean {
  const roles = useRoles();
  return roles.includes(role);
}

/**
 * True when the current user is an admin in the given env: either the
 * blanket `admin` role (break-glass, all envs) or the env-scoped
 * `admin:<env>` role.
 */
export function useIsAdminFor(env: EnvName): boolean {
  const roles = useRoles();
  return roles.includes("admin") || roles.includes(`admin:${env}`);
}
